import { createSelector } from "@reduxjs/toolkit"
import { asyncAction } from "../common"
import { limsRequest } from "./limsRequest"

// Shared SST-template library (F4, SPEC-036 / PRD-008; BE companion SPEC-019). A team-scoped,
// VERSIONED catalogue of reusable System-Suitability-Test criterion sets, tagged by INSTRUMENT
// TYPE (HPLC / GC). A lab-leader curates a few templates in one place; at run time the SST panel
// offers the LIVE templates for the analysis's instrument type and pre-fills the criteria from the
// pick. The per-run SST and the analysis-definition SST spec keep their inline criteria as a
// FALLBACK — a definition/run without a template still works unchanged (back-compatible).
//
// Versioned change control (GMP traceability): a template is never edited in place. `createVersion`
// inserts a new DRAFT version; a signed SST denormalizes the version it used (on the result) so a
// historical SST shows the exact criteria applied. The GET returns EVERY version (draft/reviewer-
// approved/effective/superseded) so the admin screen can show history + drive approvals.
//
// SPEC-039 / F7: a new version is now under reviewer→supervisor e-sign change control. It starts
// `draft` and becomes the LIVE picker option only once a reviewer e-signs it (draft →
// reviewer-approved) and a supervisor e-signs it (reviewer-approved → effective; prior effective →
// superseded). Consumers pick the EFFECTIVE one (`state === "effective"`) for the run-time picker.
//
// Persisted via /lims/sst-templates (GET open to any LIMS user; POST + POST /<id> gated server-side
// as capability-admin; the two approve sub-routes are e-signed). With showLims off this slice is
// inert (no SST UI mounts).

// The instrument types a template can be tagged with, known at build time. Mirrors the backend
// SST_INSTRUMENT_TYPES; extending it is a data + caller edit, not a code rewrite.
export const SST_INSTRUMENT_TYPES = ["HPLC", "GC"]

// SPEC-039 / F7 — the SST-template version state machine (mirrors the backend SST_TEMPLATE_STATES
// and the forskrift / spec state machine). Only an EFFECTIVE version is live for the run picker.
export const SST_TEMPLATE_STATES = {
  DRAFT: "draft",
  REVIEWER_APPROVED: "reviewer-approved",
  EFFECTIVE: "effective",
  SUPERSEDED: "superseded",
}

// i18n key for an instrument-type label (admin screen + picker + config dropdown).
export function instrumentTypeLabelKey(type) {
  if (type === "HPLC") return "limsSstInstrumentHplc"
  if (type === "GC") return "limsSstInstrumentGc"
  return ""
}

const initialState = { items: [], isLoading: false }

// Shared action type for both create() and createVersion() (a new version is just another insert,
// so it folds into the same reducer case — one place to maintain the append logic). The reducer
// replaces a same-id row if present (the just-superseded one re-arrives via a later fetch anyway)
// and otherwise appends.
const ADD_ACTION = "limsSstTemplates/add"

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "limsSstTemplates/fetch/BEGIN":
      return { ...state, isLoading: true }
    case "limsSstTemplates/fetch":
      return { items: action.res, isLoading: false }
    case ADD_ACTION: {
      const item = action.res
      if (!item) return state
      const exists = state.items.some((x) => x.id === item.id)
      return {
        ...state,
        items: exists ? state.items.map((x) => (x.id === item.id ? item : x)) : [...state.items, item],
      }
    }
    case "auth/logout":
      return initialState
    default:
      return state
  }
}

// ---- Thunks ----------------------------------------------------------------------------------

export const fetch = asyncAction("limsSstTemplates/fetch", (_dispatch) => {
  return limsRequest("GET", "/lims/sst-templates").then((res) => res.items)
})

// Create version 1 of a new template (capability-admin only; 403 otherwise). `template` carries
// name + instrumentType + the criteria. SPEC-039 / F7: the new v1 starts as a DRAFT — it becomes
// live only after the reviewer→supervisor e-sign. Returns the created (v1, draft) record.
export const create = asyncAction(ADD_ACTION, (_dispatch, template) => {
  return limsRequest("POST", "/lims/sst-templates", { input: template }).then((res) => res.item)
})

// Create the NEXT version of an existing template as a DRAFT (admin only). `id` may be any version
// in the lineage; the server inserts the bump in `draft` and does NOT supersede the current live
// version (it stays usable while the revision is reviewed — SPEC-039 / F7). `template` carries the
// new criteria (the admin pre-fills from the version being edited). Returns the new draft version.
export const createVersion = asyncAction(ADD_ACTION, (_dispatch, id, template) => {
  return limsRequest("POST", `/lims/sst-templates/${id}`, { input: template }).then((res) => res.item)
})

// Reviewer-approves a draft template version: draft → reviewer-approved. The signer's password +
// reason ride at the request-body TOP LEVEL; the server enforces the "reviewer" capability + 4-eyes
// (≠ the version creator). (SPEC-039 / F7.) Returns the updated version.
export const reviewerApprove = asyncAction(ADD_ACTION, (_dispatch, id, signature) => {
  return limsRequest("POST", `/lims/sst-templates/${id}/reviewer-approve`, {
    password: (signature && signature.password) || undefined,
    reason: (signature && signature.reason) || undefined,
  }).then((res) => res.item)
})

// Supervisor-approves a reviewer-approved template version: → effective; the server supersedes any
// prior effective version of the same lineage in the same transaction. "supervisor" capability,
// 4-eyes (≠ the version creator AND the reviewer signer). (SPEC-039 / F7.) Returns the updated
// version; a subsequent `fetch()` reflects the superseded prior version.
export const supervisorApprove = asyncAction(ADD_ACTION, (_dispatch, id, signature) => {
  return limsRequest("POST", `/lims/sst-templates/${id}/supervisor-approve`, {
    password: (signature && signature.password) || undefined,
    reason: (signature && signature.reason) || undefined,
  }).then((res) => res.item)
})

// ---- Selectors -------------------------------------------------------------------------------

export const selectSstTemplates = (state) => state.limsSstTemplates.items

// A single template (any version) by id, or undefined.
export const selectTemplate = (state, id) =>
  id ? state.limsSstTemplates.items.find((x) => x.id === id) : undefined

// The EFFECTIVE templates for a given instrument type — what the run-time picker offers. SPEC-039 /
// F7: "live" is now `state === "effective"` (a draft / reviewer-approved version is NOT yet usable,
// even though it has no `supersededAt`). Memoised per instrument type so the dropdown doesn't
// re-render on unrelated state changes. A blank/unknown instrument type yields none (the picker then
// stays hidden / inline-only).
const selectByInstrumentTypeMemo = createSelector(
  [(state) => state.limsSstTemplates.items, (_state, instrumentType) => instrumentType],
  (items, instrumentType) =>
    instrumentType
      ? items
          .filter((tpl) => tpl.instrumentType === instrumentType && tpl.state === SST_TEMPLATE_STATES.EFFECTIVE)
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      : []
)
export const selectByInstrumentType = (state, instrumentType) =>
  selectByInstrumentTypeMemo(state, instrumentType)
